import React, { useState } from 'react';
import styled from 'styled-components';
import { Text, TextInput } from '@cuemath/web-comps';
import { COLORS, FlexView, Row } from '@cuemath/comps';

import TransferStatusCards from 'comps/trasfer-status-cards';

import { getLicenseDetailsText } from 'utils/student-transfer';
import {
  EditSlotIcon,
  EditSlotWrapper,
} from '../../student-transfer/styles/transfer-info';

const HeaderTitle = styled(FlexView)`
  gap: 16px;
`;

const TitleText = styled(Text)`
  ::first-letter {
    text-transform: uppercase;
  }
`;

const TeacherDetails = styled(Row)`
  width: 464px;
  padding: 16px;
  gap: 12px;
  border-radius: 8px;
  background-color: ${COLORS.CLOUD};
`;

const TeacherImage = styled.image`
  width: 44px;
  height: 44px;
`;

const TeacherName = styled(FlexView)`
  gap: 4px;
  width: 408px;
`;

const TransferDetailsCard = styled(Row)`
  padding: 0;
  gap: ${({ gap }) => gap || 4}px;
`;

const PreferredTeacherCard = styled(FlexView)`
  gap: 16px;
`;

const TransferSlotStatus = ({
  studentName,
  transferDetails,
  transferReason,
  setPreferredTeacherData,
  preferredTeacherData,
  preferredTeacherReasonSelected,
}) => {
  const [editTeacherId, setEditTeacherId] = useState(false);

  const {
    profile_pic_url: profilePicUrl,
    status: transferStatus,
    teacher_name: teacherName,
    licensed_geos: licensedGeos,
    licensed_grades: licensedGrades,
    specialization,
    preferred_teacher_id: preferredTeacherId,
    reason: transferReasonKey,
  } = transferDetails || {};

  // Two-phase admin transfer: after "Start transfer process" the request sits
  // at INITIATED with no teacher yet. Show a clear in-process banner instead of
  // the legacy "slots pending" framing.
  const isAdminTransferInProcess =
    transferReasonKey === 'ADMIN_DIRECT_TRANSFER' &&
    transferStatus === 'INITIATED' &&
    !teacherName;

  const licenseDetailsText = getLicenseDetailsText({
    licensedGeos,
    licensedGrades,
    specialization,
  });

  const handleEditPreferredTeacherId = () => {
    if (editTeacherId) {
      setPreferredTeacherData(prev => ({ ...prev, preferredTeacherId: '' }));
    }
    setEditTeacherId(!editTeacherId);
  };

  return (
    <>
      <HeaderTitle alignItems="flex-start" justifyContent="center">
        <TitleText variant="h6">
          {isAdminTransferInProcess
            ? `${studentName}'s transfer is in process — assign a new teacher to complete it`
            : `${studentName}'s smooth transfer is just around the corner`}
        </TitleText>

        {teacherName && (
          <TeacherDetails alignItems="flex-start" justifyContent="flex-start" nowrap>
            {false && profilePicUrl && (
              <TeacherImage src={profilePicUrl} alt={studentName[0]} />
            )}
            <TeacherName alignItems="flex-start" justifyContent="center">
              <Text variant="body1Medium">{teacherName || '-'}</Text>
              <Text variant="body2">{licenseDetailsText || '-'}</Text>
            </TeacherName>
          </TeacherDetails>
        )}
      </HeaderTitle>

      <TransferDetailsCard nowrap alignItems="center" justifyContent="flex-start">
        <Text variant="body1Regular">Reason for transfer:</Text>
        <Text variant="body1Medium">{transferReason || '-'}</Text>
      </TransferDetailsCard>

      <TransferDetailsCard nowrap alignItems="center" justifyContent="flex-start" gap={8}>
        <Text variant="body1Regular">Transfer Status:</Text>
        <TransferStatusCards cardType={transferStatus} />
      </TransferDetailsCard>

      {(preferredTeacherReasonSelected || isAdminTransferInProcess) && (
        <PreferredTeacherCard>
          <TransferDetailsCard
            nowrap
            alignItems="center"
            justifyContent="flex-start"
            gap={8}
          >
            <Text variant="body1Regular">Selected preferred teacher id:</Text>
            <Text variant="body1Medium">{preferredTeacherId || '-'}</Text>

            <EditSlotWrapper>
              <EditSlotIcon onClick={handleEditPreferredTeacherId} />
            </EditSlotWrapper>
          </TransferDetailsCard>
          {/* On the admin two-phase in-process state no teacher is assigned yet,
              so show the Teacher ID input by default (not behind the edit toggle)
              — the admin must enter the target teacher to complete the transfer. */}
          {(editTeacherId || isAdminTransferInProcess) && (
            <TextInput
              value={preferredTeacherData?.preferredTeacherId || ''}
              onChange={value =>
                setPreferredTeacherData(prev => ({ ...prev, preferredTeacherId: value }))
              }
              width="320px"
              label="Teacher ID"
            />
          )}
        </PreferredTeacherCard>
      )}
    </>
  );
};

export default TransferSlotStatus;
